import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import RecipeCard from "components/recipe/RecipeCard";

const ROW_HEIGHT = 8; // matches grid-auto-rows in the CSS
const ROW_GAP = 20; // vertical space between cards

const useGridRowSpan = (trigger) => {
  const ref = useRef(null);
  const [span, setSpan] = useState(1);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const height = el.getBoundingClientRect().height;
    setSpan(Math.ceil((height + ROW_GAP) / ROW_HEIGHT));
  }, []);

  // Keep watching for size changes (images loading, text wrapping)
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  // Measure immediately when the card opens or closes, before the snapshot
  useLayoutEffect(() => {
    measure();
  }, [trigger, measure]);

  return [ref, span];
};

const RecipeGridItem = ({ recipe, isExpanded, onToggle }) => {
  const [cardRef, span] = useGridRowSpan(isExpanded);

  useEffect(() => {
    if (isExpanded && window.innerWidth < 601) {
      requestAnimationFrame(() =>
        cardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        }),
      );
    }
  }, [isExpanded, cardRef]);

  const classes = [
    "Recipe-Grid-Item",
    recipe.status === "pending_approval" && "Pending",
    isExpanded && "Expanded",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      id={`recipe-${recipe.id}`}
      className={classes}
      style={{
        gridRowEnd: `span ${span}`,
        viewTransitionName: `recipe-${recipe.id}`,
      }}
    >
      <div ref={cardRef}>
        <RecipeCard
          recipe={recipe}
          isExpanded={isExpanded}
          emitToggle={onToggle}
        />
      </div>
    </div>
  );
};

export default RecipeGridItem;
